import { CircularProgress, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { flatMap, isEmpty, map, uniq } from 'es-toolkit/compat';
import React, { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { AnnotationAssignment, TodoOption } from 'interfaces/annotation';
import { Model } from 'interfaces/model';
import { useFilterAndFlattenAnnotationAssignments } from './helpers';
import { TargetClassRow } from './TargetClassRow';
import { IFormValues } from './types';

interface Props {
  formMethods: UseFormReturn<Partial<IFormValues>, any>;
  modelDetailsResponse?: Model;
  annotationAssignments: AnnotationAssignment[];
  modelDetailsErrorMessage?: string;
  isLoading: boolean;
}

export const TargetClassesForTrainingStep: React.FC<Props> = ({
  formMethods,
  annotationAssignments,
  modelDetailsResponse,
  modelDetailsErrorMessage,
  isLoading,
}) => {
  const {
    watch,
    formState: { errors },
  } = formMethods;

  const selectedAssignmentIds = watch('annotationAssignmentIds');
  const slideIds = watch('slideIds') || [];

  const classesConfigFromModel = modelDetailsResponse?.meta?.deps?.classesConfig;

  const filteredAndFlattenedAnnotationAssignments = useFilterAndFlattenAnnotationAssignments({
    annotationAssignments,
    selectedAssignmentIds,
    selectedSlideIds: slideIds,
  });

  const availableOptions: TodoOption[] = useMemo(
    () => uniq(flatMap(filteredAndFlattenedAnnotationAssignments, 'options')),
    [filteredAndFlattenedAnnotationAssignments]
  );

  const availableOptionNames = useMemo(() => uniq(map(availableOptions, 'name')), [availableOptions]);

  return isLoading ? (
    <Grid item>
      <CircularProgress size={16} sx={{ mr: 1 }} />
      Loading model details...
    </Grid>
  ) : isEmpty(classesConfigFromModel) ? (
    <Grid item>
      <Typography color="error">
        {modelDetailsErrorMessage
          ? modelDetailsErrorMessage
          : errors.baseModel
          ? 'Please fix the errors in previous steps to see available options'
          : 'No base model class config available. Please check you model selection'}
      </Typography>
    </Grid>
  ) : isEmpty(availableOptions) ? (
    <Grid item>
      <Typography color="error">
        {errors.annotationAssignmentIds
          ? 'Please fix the errors in previous steps to see available options'
          : 'No available options to assign. Please check your annotation assignments and selected slides'}
      </Typography>
    </Grid>
  ) : (
    <>
      <Grid item>
        <Table size="small" aria-label="purchases">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 10 }} />
              <TableCell sx={{ width: 10 }}>Enabled</TableCell>
              <TableCell sx={{ width: 50 }}>Target class</TableCell>
              <TableCell>Annotation options for class</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {map(classesConfigFromModel, (classConfig, targetClass) => (
              <TargetClassRow
                key={targetClass}
                targetClass={targetClass}
                formMethods={formMethods}
                classConfig={classConfig}
                availableOptions={availableOptions}
                availableOptionNames={availableOptionNames}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
    </>
  );
};
